import { AWARD_META } from '@ringmaster/shared';
import type { SittingGame } from '../net/sitting.js';
import { gameById } from '../assets/manifest.js';

/**
 * The two paste-ready texts a hot table's host can take away with them —
 * "Book the next show" and "The Playbill" (issue #55, the Herald's Phase 0).
 *
 * WHY TEXT AND NOT A BOT. The Herald (#2) would post both of these into the
 * guild's channel by itself, and is blocked on a bot token. Before asking for
 * one, the cheapest possible test of its premise: will a host paste a
 * pre-written line into their own channel at the moment their table is
 * hottest? If not, automating the same line is not the bet. So these are
 * deliberately PLAIN — no LLM, no embed, no link — text a person copies.
 *
 * Everything here is pure and unit-tested: the callers own the clipboard, the
 * telemetry, and the decision to offer it at all (ui/NextShowStrip.tsx).
 *
 * Emoji appear in the OUTPUT, which is a Discord message someone pastes into a
 * channel — not in the app's own chrome, where the ban still stands (Glyph).
 */

/**
 * The PEOPLE at the table — troupe seats (issue #4) filtered out.
 *
 * A bot seat is disclosed everywhere it appears in the app: the troupe mask,
 * the "AI" tag, no presence dot. NONE of that disclosure travels into a text
 * the host pastes into their channel, so an unfiltered count would announce
 * "6 seats at the table" for two humans and four bots, and the Playbill would
 * rank troupe masks among the guild. Both numbers this module prints are about
 * who was actually there.
 *
 * `isAi` is absent on a human seat and only ever `true` on a troupe one
 * (server-patched in room.ts), so the predicate is a strict compare.
 */
export function humanSeats<T extends { name: string; isAi?: boolean }>(roster: readonly T[]): T[] {
  return roster.filter((p) => p.isAi !== true);
}

/** A sitting is "hot" from its second finished game — the point a guild has stayed. */
export const HOT_SITTING_GAMES = 2;

/** True once this room has finished enough games this visit to be worth booking. */
export function isHotSitting(games: number): boolean {
  return games >= HOT_SITTING_GAMES;
}

/** Display name for a game id, falling back to the id itself for an unknown one. */
function gameName(gameId: string): string {
  return gameById(gameId)?.name ?? gameId;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Which game this sitting was really about — the most-played, ties going to the
 * one played most recently (a guild that switched mid-sitting is most likely to
 * come back to what it ended on).
 */
export function headlineGame(log: SittingGame[]): string {
  const counts = new Map<string, number>();
  for (const g of log) counts.set(g.gameId, (counts.get(g.gameId) ?? 0) + 1);
  let best = log[log.length - 1]?.gameId ?? 'bloodmoon';
  for (const g of [...log].reverse()) {
    if ((counts.get(g.gameId) ?? 0) > (counts.get(best) ?? 0)) best = g.gameId;
  }
  return best;
}

/**
 * The channel announcement. Defaults to "same time next week" in words rather
 * than reading a weekday and hour off the host's clock: the client's timezone
 * is not the guild's, a pasted "Friday 8pm" that is wrong for half the channel
 * is worse than none, and "same time next week" is the sentence a returning
 * table actually needs (issue #55's open question — implementer's call, stated
 * so the owner can overrule it).
 */
export function nextShowAnnouncement(log: SittingGame[], players: number): string {
  const game = gameName(headlineGame(log));
  return [
    '🩸 The Ringmaster returns — same time next week.',
    '',
    `Tonight this guild played ${plural(log.length, 'game', 'games')} of ${game}, ` +
      `${plural(players, 'seat', 'seats')} at the table.`,
    'Same channel, same hour, seven nights from now.',
    '',
    "React 🩸 if you're in and I will keep your chair.",
  ].join('\n');
}

/** How an act's ending is worded — each game in its own register. */
function outcomeLine(g: SittingGame): string {
  if (g.gameId === 'whisper') {
    return g.winner === 'VILLAGE' ? 'the word was found' : 'the candle died';
  }
  const side = g.winner === 'VILLAGE' ? 'the Village took it' : 'the Wolves took it';
  return g.atDawn ? `${side}, at dawn` : side;
}

/** Who the reveal exposed — or the honest note that nobody was hiding. */
function traitorLine(g: SittingGame): string {
  if (g.traitors.length === 0) {
    return g.traitorKind === 'deceiver' ? 'No Deceiver was dealt.' : 'No wolf was ever in play.';
  }
  const who = g.traitors.join(', ');
  if (g.traitorKind === 'deceiver') return `Deceiver: ${who}.`;
  return g.traitors.length === 1 ? `Wolf: ${who}.` : `Wolves: ${who}.`;
}

/** Roman numerals for the acts — the log is capped well under 40. */
export function roman(n: number): string {
  const table: [number, string][] = [
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let left = n;
  let out = '';
  for (const [value, glyph] of table) {
    while (left >= value) {
      out += glyph;
      left -= value;
    }
  }
  return out;
}

/**
 * The sitting recap. Built from the sitting log (END public data only — see
 * net/sitting.ts) plus the room's cumulative scoreboard, which is the same
 * ledger the END screen has been showing all along.
 *
 * Deliberately THIS SITTING ONLY: The Standing's weekly rank movement exists on
 * `/api/standing`, but a recap that mixes tonight with the week reads as two
 * documents (issue #55's second open question — default taken, overrulable).
 */
export function playbillText(log: SittingGame[], standing: { name: string; score: number }[]): string {
  const lines: string[] = [
    `🎪 The Playbill — ${plural(log.length, 'game', 'games')} with this guild`,
    '',
  ];
  log.forEach((g, i) => {
    const award = g.topAward ? ` ${AWARD_META[g.topAward.code].label}: ${g.topAward.name}.` : '';
    lines.push(`${roman(i + 1)} · ${gameName(g.gameId)} — ${outcomeLine(g)}. ${traitorLine(g)}${award}`);
  });
  const ranked = [...standing].sort((a, b) => b.score - a.score);
  if (ranked.length > 0) {
    lines.push('', 'The standing when the curtain fell');
    ranked.forEach((p, i) => lines.push(`${i + 1}. ${p.name} — ${plural(p.score, 'pt', 'pts')}`));
  }
  return lines.join('\n');
}
